<template>
  <div v-if="uploadProgress === -1" class="prompt-wrapper">
    <h1 class="header-1">{{ $t('animation.prompt.heading') }}</h1>

    <div class="caption input-label">{{ $t('animation.prompt.name') }}</div>
    <base-input
      class="animation-name-input"
      type="text"
      :placeholder="$t('animation.prompt.name')"
      v-model="animationName"
    />

    <div class="input-form">
      <textarea
        v-model="prompt"
        class="prompt-text"
        :class="{ 'error-border': characterCount > 255 }"
        :placeholder="$t('animation.prompt.writePrompt')"
        @input="updateCharacterCount"
        rows="5"
      ></textarea>

      <div class="input-form-bottom">
        <div class="caption" :class="{ 'error-text': characterCount > 255 }">
          {{ characterCount }} / 255
        </div>
      </div>
    </div>

    <PrimaryBtn
      class="action-btn"
      :disabled="isCreateDisabled"
      @click="proccessPrompt"
    >
      {{ $t('animation.prompt.create') }}
    </PrimaryBtn>

    <div v-if="hasInsufficientCredits" class="credit-nudge">
      {{ $t('animation.prompt.noCredits') }}
      <router-link to="/app/plan" class="credit-nudge-link">{{ $t('animation.prompt.upgrade') }}</router-link>
    </div>

    <div class="settings-row">
      <div class="settings-column">
        <div class="caption input-label model-label-row">{{ $t('animation.prompt.seconds') }}</div>
        <base-input
          class="seconds-input"
          type="number"
          :placeholder="$t('animation.prompt.seconds')"
          :modelValue="String(seconds)"
          @update:modelValue="(val) => (seconds = Number(val))"
        />
      </div>

      <div class="settings-column">
        <div class="caption input-label model-label-row">{{ $t('animation.prompt.model') }}</div>
        <BasePicklist
          v-model="selectedModel"
          :options="modelOptions"
          :placeholder="$t('animation.prompt.selectModel')"
        />
      </div>
    </div>

    <div class="caption input-label">{{ $t('animation.prompt.examples') }}</div>
    <div class="prompt-examples caption">
      <div class="example-list">
        <div v-for="(example, index) in promptExamples" :key="index" class="example-item">
          <div class="example-text" @click="copyExample(example)">{{ example }}</div>
          <button class="copy-btn" @click="copyExample(example)">
            <span class="copy-icon">⎘</span>
          </button>
        </div>
      </div>
      <div class="examples-note">
        {{ $t('animation.prompt.examplesNote') }}
      </div>
    </div>
  </div>

  <div v-else class="progress-container">
    <ProgressSpinner
      :progress="uploadProgress"
      :is-processing="!showDebugProcessingProgress"
      :label="$t('animation.prompt.processing')"
      :subtitle="$t('animation.prompt.generating')"
    />
  </div>

  <!-- Notification Toast -->
  <NotificationToast
    :show="showNotification"
    :message="notificationMessage"
    :is-error="isErrorNotification"
    @close="closeNotification"
  />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import PrimaryBtn from "@/components/BaseComponents/PrimaryBtn.vue";
import ProgressSpinner from "@/components/BaseComponents/ProgressSpinner.vue";
import BasePicklist from "@/components/BaseComponents/BasePicklist.vue";
import NotificationToast from "@/components/BaseComponents/NotificationToast.vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { animationProcessingAPI } from "@/services/api";
import { preloadCharacterModel } from "@/utils/characterModel";
import { trackEvent, trackFormSubmission, trackError } from "@/utils/analytics";
import { useAvailability } from "@/composables/useAvailability";
import { useI18n } from 'vue-i18n';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { getServiceEntry, getDomainItemEntry } = useAvailability();
const { t, tm } = useI18n();

const prompt = ref("");
const seconds = ref(5);
const animationName = ref("");
const characterCount = ref(0);
const selectedModel = ref(process.env.VUE_APP_DEFAULT_MODEL || "gen2");

const uploadProgress = ref(-1);
const showDebugProcessingProgress = ref(false);

// Notification state
const showNotification = ref(false);
const notificationMessage = ref("");
const isErrorNotification = ref(false);
let isUnmounted = false;
let processingInterval = null;

function applyDebugPreview() {
  if (!store.getters.isDebugMode) return;

  const mode = route.query.debug;
  const progress = Number(route.query.progress);

  if (mode === "processing") {
    uploadProgress.value = Number.isFinite(progress) ? progress : 42;
    showDebugProcessingProgress.value = Number.isFinite(progress);
  }
}

function showErrorNotification(message) {
  notificationMessage.value = message;
  isErrorNotification.value = true;
  showNotification.value = true;
}

function closeNotification() {
  showNotification.value = false;
}

const hasInsufficientCredits = computed(() => {
  const isFreemium = store.state.activePlan === "Freemium";
  if (!isFreemium) return false;

  if (["gen2", "kimodo"].includes(selectedModel.value)) {
    return store.state.gen_v2_remaining <= 0;
  }
  return store.state.remainingCredits <= 0;
});

// TODO: Keep this threshold in sync with the backend and the business model.
const REMAINING_GENERATIONS_DISPLAY_THRESHOLD = 5;

const promptServiceEntry = computed(() => getServiceEntry("prompt"));
const selectedModelAvailability = computed(() =>
  buildModelAvailability(selectedModel.value)
);
const isCreateDisabled = computed(() => {
  return (
    characterCount.value === 0 ||
    !animationName.value ||
    hasInsufficientCredits.value ||
    promptServiceEntry.value.enabled === false ||
    selectedModelAvailability.value.enabled === false
  );
});

function buildModelAvailability(modelId) {
  return getDomainItemEntry("prompt", "models", modelId);
}

const modelOptions = computed(() => {
  const isFreemium = store.state.activePlan === "Freemium";
  const isCreditLimitedModel = (modelId) => ["gen2", "kimodo"].includes(modelId);
  const baseOptions = [
    {
      value: "gen1",
      label: "Gen-v1",
      description: t('animation.prompt.models.gen1'),
    },
    {
      value: "gen2",
      label: "Gen-v2",
      description:
        store.state.gen_v2_remaining < REMAINING_GENERATIONS_DISPLAY_THRESHOLD
          ? t('animation.prompt.models.gen2Remaining', { count: store.state.gen_v2_remaining })
          : t('animation.prompt.models.gen2'),
      disabled: isFreemium && store.state.gen_v2_remaining === 0,
      disabledMessage:
        isFreemium && store.state.gen_v2_remaining === 0
          ? t('animation.prompt.models.gen2Upgrade')
          : undefined,
    },
    {
      value: "asm",
      label: "ASM-v1",
      description: t('animation.prompt.models.asm'),
    },
    {
      value: "kimodo",
      label: "Kimodo",
      description:
        store.state.gen_v2_remaining < REMAINING_GENERATIONS_DISPLAY_THRESHOLD
          ? t('animation.prompt.models.kimodoRemaining', { count: store.state.gen_v2_remaining })
          : t('animation.prompt.models.kimodo'),
    },
  ];

  return baseOptions
    .map((option) => {
      const availabilityEntry = buildModelAvailability(option.value);
      const isCreditBlocked =
        isCreditLimitedModel(option.value) && isFreemium && store.state.gen_v2_remaining === 0;
      const isAvailabilityBlocked = availabilityEntry.enabled === false;
      const disabled = isAvailabilityBlocked || isCreditBlocked;

      return {
        ...option,
        disabled,
        disabledMessage:
          availabilityEntry.message ||
          (isCreditBlocked ? t('animation.prompt.models.gen2Upgrade') : undefined),
        mode: availabilityEntry.mode,
      };
    })
    .filter((option) => option.mode !== "hidden");
});

const promptExamples = computed(() => tm('animation.prompt.examplesList'));

function copyExample(exampleText) {
  // Track example usage
  trackEvent('prompt_example_used', {
    example_text: exampleText,
    selected_model: selectedModel.value,
  });

  prompt.value = exampleText;
  updateCharacterCount();
}

function updateCharacterCount() {
  characterCount.value = prompt.value.length;
}

// Watch for model selection changes
watch(selectedModel, (newModel, oldModel) => {
  if (oldModel) { // Only track if there was a previous value (not initial load)
    trackEvent('model_selected', {
      model: newModel,
      previous_model: oldModel,
      user_plan: store.state.activePlan,
    });
  }
});

watch(
  modelOptions,
  (options) => {
    const selectedOption = options.find((option) => option.value === selectedModel.value);
    if (selectedOption && !selectedOption.disabled) {
      return;
    }

    const firstAvailableOption = options.find((option) => !option.disabled);
    if (firstAvailableOption) {
      selectedModel.value = firstAvailableOption.value;
    }
  },
  { immediate: true }
);

onMounted(async () => {
  applyDebugPreview();

  const now = new Date();
  const dateStr = now.toISOString().split("T")[0];
  const timeStr = now.toTimeString().split(" ")[0];
  animationName.value = `${dateStr} ${timeStr}`;

  // Fetch user details to get current plan
  await store.dispatch("fetchUserDetails");
});

onBeforeUnmount(() => {
  isUnmounted = true;
  if (processingInterval) {
    clearInterval(processingInterval);
    processingInterval = null;
  }
});

async function proccessPrompt() {
  await store.dispatch("fetchAvailability");
  if (isUnmounted) return;

  if (promptServiceEntry.value.enabled === false) {
    showErrorNotification(
      promptServiceEntry.value.message || t('animation.prompt.unavailable')
    );
    return;
  }

  if (selectedModelAvailability.value.enabled === false) {
    showErrorNotification(
      selectedModelAvailability.value.message || t('animation.prompt.modelUnavailable')
    );
    return;
  }

  if (characterCount.value >= 255) {
    showErrorNotification(t('animation.prompt.maxCharacters'));
    return;
  }

  if (!animationName.value) {
    showErrorNotification(t('animation.prompt.nameRequired'));
    return;
  }

  const sessionId = store.getters.getSessionId;
  const promptData = {
    prompt: prompt.value,
    duration: seconds.value,
    fps: 30,
    temperature: 0.7,
    name: animationName.value,
    model: selectedModel.value,
  };

  try {
    uploadProgress.value = 0;
    simulateProcessing();

    // Track animation creation start
    trackEvent('animation_creation_started', {
      method: 'prompt',
      model: selectedModel.value,
      duration: seconds.value,
      prompt_length: prompt.value.length,
      user_plan: store.state.activePlan,
    });

    const result = await animationProcessingAPI.processPrompt(promptData, sessionId);
    void preloadCharacterModel();
    if (isUnmounted) return;

    // Update credits from response
    if (result.remaining_credits !== undefined) {
      store.commit("setRemainingCredits", result.remaining_credits);
    }
    if (result.gen_v2_remaining !== undefined) {
      store.commit("setGenV2Remaining", result.gen_v2_remaining);
    }
    if (result.hashed_id) {
      // Track successful animation creation
      trackFormSubmission('prompt_animation_form', true);
      trackEvent('animation_created', {
        animation_id: result.hashed_id,
        method: 'prompt',
        model: selectedModel.value,
        duration: seconds.value,
      });

      router.push({ path: "/animation", query: { animationId: result.hashed_id } });
    } else {
      throw new Error("No hashed_id in response");
    }
  } catch (error) {
    if (isUnmounted) return;
    console.error("Error processing prompt:", error);

    // Track error
    trackFormSubmission('prompt_animation_form', false);
    trackError('animation_creation_error', error.message || 'Unknown error', 'prompt_form');

    if (error.status === 500) {
      showErrorNotification(
        t('animation.prompt.generationLimit')
      );
    } else {
      showErrorNotification(
        t('animation.prompt.processingError', { error: error.message || 'Unknown error' })
      );
    }
    resetUploadState();
  }
}

function simulateProcessing() {
  const totalTime = 10000;
  const intervalTime = 100;
  const increments = totalTime / intervalTime;
  let currentProgress = 0;

  if (processingInterval) clearInterval(processingInterval);

  processingInterval = setInterval(() => {
    if (isUnmounted) {
      clearInterval(processingInterval);
      processingInterval = null;
      return;
    }
    currentProgress += 100 / increments;
    if (currentProgress >= 99) {
      clearInterval(processingInterval);
      processingInterval = null;
      uploadProgress.value = 99;
    } else {
      uploadProgress.value = currentProgress;
    }
  }, intervalTime);
}

function resetUploadState() {
  if (processingInterval) {
    clearInterval(processingInterval);
    processingInterval = null;
  }
  uploadProgress.value = -1;
  showDebugProcessingProgress.value = false;
}
</script>

<style scoped>
.header-1 {
  text-align: center;
}

.prompt-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xl);
  width: 60%;
}

.input-form,
.prompt-description {
  width: 100%;
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-xl);
  background: var(--bg-card);
  box-sizing: border-box;
}

.prompt-text {
  width: 100%;
  background: transparent;
  border: none;
  color: var(--color-white);
}

.prompt-text:focus {
  outline: none;
  border: none;
}

.prompt-text.error-border {
  border: 1px solid red;
}

.input-form-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-btn:disabled {
  background: var(--color-white-20);
  color: var(--color-white-20);
  cursor: not-allowed;
}

.caption {
  color: var(--color-white-40);
}

.caption.error-text {
  color: red;
}

.seconds-input,
.animation-name-input {
  width: 100%;
  box-sizing: border-box;
}

.prompt-description {
  width: 100%;
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-xl);
  background: var(--bg-card);
}

.prompt-examples {
  width: 100%;
  padding: var(--spacing-xl);
  border-radius: var(--border-radius-xl);
  background: var(--bg-card);
  box-sizing: border-box;
}

.examples-title {
  font-size: var(--font-size-body);
  margin-bottom: var(--margin-sm);
  color: var(--color-white-70);
}

.input-label {
  width: 100%;
  text-align: left;
  margin-top: var(--spacing-xl);
}

.example-list {
  display: flex;
  flex-direction: column;
  gap: var(--gap-md);
  margin-bottom: var(--spacing-xl);
}

.example-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--padding-md);
  background: var(--color-white-05);
  border-radius: var(--border-radius-md);
  cursor: pointer;
}

.example-item:hover {
  background-color: var(--color-white-40);
}

.example-text {
  flex-grow: 1;
  color: var(--color-white-70);

}

.copy-btn {
  background: transparent;
  border: none;
  color: var(--color-white-50);
  cursor: pointer;
  font-size: var(--font-size-body);
  padding: var(--padding-sm);
  border-radius: var(--border-radius-xs);
  transition: var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-btn:hover {
  background: var(--bg-card);
  color: var(--color-white-80);
}

.copy-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.examples-note {
  font-size: var(--font-size-caption-sm);
  color: var(--color-white-40);
}

.progress-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: var(--spacing-3xl);
}

.settings-row {
  display: flex;
  gap: var(--spacing-xl);
  width: 100%;
}

.settings-column {
  flex: 1;
}

.model-label-row {
  display: flex;
  align-items: center;
  gap: 0.3em;
  height: 2rem;
}

.credit-nudge {
  color: var(--color-white-40);
  font-size: var(--font-size-caption);
  text-align: center;
}

.credit-nudge-link {
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
  font-weight: var(--font-weight-medium);
}

.credit-nudge-link:hover {
  text-decoration: underline;
}
</style>
